/**
 * Info icon with hover tooltip. Use for explaining terms and features
 * to users. Pass `text` for a plain string, or `content` for a React node
 * when the explanation needs structure (title + body, lists, etc.).
 *
 * Backward compatible with the existing `text` callers — the styling
 * and width got a modern refresh but the prop surface is unchanged.
 */
import React from 'react';

export default function InfoTooltip({
  text,
  content,
  title,
  className = '',
  width = 360,          // px — bump for feature-explanation use; callers can tighten
  side = 'top',         // 'top' | 'bottom'
}) {
  const sideClass =
    side === 'bottom'
      ? 'top-full mt-2'
      : 'bottom-full mb-2';

  const arrowClass =
    side === 'bottom'
      ? '-top-1 border-t-0 border-l-transparent border-r-transparent border-b-slate-900'
      : '-bottom-1 border-b-0 border-l-transparent border-r-transparent border-t-slate-900';

  return (
    <span className={`group/tip relative inline-flex align-middle ${className}`}>
      <span
        className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-slate-200/80 text-slate-500 text-[10px] font-semibold leading-none transition-colors hover:bg-slate-300 hover:text-slate-700 cursor-help"
        aria-label={typeof text === 'string' ? text : (title || 'More info')}
      >
        i
      </span>
      <span
        role="tooltip"
        style={{ width }}
        className={`pointer-events-none absolute left-1/2 -translate-x-1/2 z-50 ${sideClass} rounded-xl bg-slate-900 text-white shadow-xl ring-1 ring-slate-700/60 px-3.5 py-2.5 opacity-0 translate-y-0.5 transition-all duration-150 group-hover/tip:opacity-100 group-hover/tip:translate-y-0`}
      >
        {title && (
          <span className="block text-[11px] font-semibold text-white tracking-tight mb-1">
            {title}
          </span>
        )}
        {content ? (
          <span className="block text-[11.5px] leading-snug text-slate-200 font-normal">
            {content}
          </span>
        ) : (
          <span className="block text-[11.5px] leading-snug text-slate-200 font-normal">
            {text}
          </span>
        )}
        <span
          className={`absolute left-1/2 -translate-x-1/2 h-0 w-0 border-solid border-4 ${arrowClass}`}
        />
      </span>
    </span>
  );
}
